import React, { useState, useEffect } from "react";
import GridLayout, { WidthProvider, Layout, Layouts } from "react-grid-layout";
import { DashboardWidget, DashboardArea, WidgetInstance } from "../DashboardWidgetConfig";
import { LEFT_COLS, RIGHT_COLS, DashboardBreakpoint } from "../../utils/generateLayouts";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const WidthAwareGridLayout = WidthProvider(GridLayout);

interface AreaGridLayoutProps {
    area: DashboardArea;
    currentBp: DashboardBreakpoint;
    instances: WidgetInstance[];
    layouts: Layouts;
    onLayoutChange: (layout: Layout[]) => void;
    onDeleteWidget?: (instanceId: string) => void;
    onDragStop?: () => void;
    widgetTypes: DashboardWidget[];
}

const GridItem: React.FC<{
    instance: WidgetInstance;
    onDelete: () => void;
    onEdit: () => void;
    widgetTypes: DashboardWidget[];
}> = ({ instance, onDelete, onEdit, widgetTypes }) => {
    const widget = widgetTypes.find((w) => w.type === instance.type);
    const borderless = widget?.borderless ?? false;

    return (
        <div
            style={{
                height: "100%",
                width: "100%",
                color: "white",
                display: "flex",
                flexDirection: "column",
                gap: borderless ? 0 : 8,
                alignItems: "center",
                border: borderless ? "none" : "1px solid #b0b0b0",
                padding: borderless ? 0 : 10,
                borderRadius: 8,
                overflow: "hidden",
            }}
        >
            {widget?.content({
                onDelete,
                onEdit,
                config: instance.config,
                instanceId: instance.instanceId,
            })}
        </div>
    );
};

/**
 * A single area grid layout component.
 * Used for both left (4-column) and right (2-column) dashboard areas.
 */
export const AreaGridLayout: React.FC<AreaGridLayoutProps> = ({
    area,
    currentBp,
    instances,
    layouts,
    onLayoutChange,
    onDeleteWidget,
    onDragStop,
    widgetTypes,
}) => {
    const colsPerBp = area === 'left' ? LEFT_COLS : RIGHT_COLS;
    const cols = colsPerBp[currentBp];
    const layout = layouts[currentBp] ?? [];
    const itemMargin: [number, number] = area === 'left' ? [21, 21] : [13, 13];

    // Disable CSS transitions on initial render to prevent visual shuffle
    const [transitionsEnabled, setTransitionsEnabled] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setTransitionsEnabled(true), 150);
        return () => clearTimeout(timer);
    }, []);

    return (
        <WidthAwareGridLayout
            className={`layout layout-${area}${transitionsEnabled ? '' : ' no-transition'}`}
            cols={cols}
            rowHeight={40}
            margin={itemMargin}
            containerPadding={[0, 0]}
            layout={layout}
            onLayoutChange={(next) => onLayoutChange(next)}
            onDragStop={() => onDragStop?.()}
            draggableCancel=".no-drag"
            isDraggable={true}
            isResizable={false}
            compactType="vertical"
            preventCollision={false}
            useCSSTransforms={false}
        >
            {instances.map((instance) => (
                <div key={instance.instanceId}>
                    <GridItem
                        instance={instance}
                        onDelete={() => onDeleteWidget?.(instance.instanceId)}
                        onEdit={() => {
                            console.log("Edit not implemented");
                        }}
                        widgetTypes={widgetTypes}
                    />
                </div>
            ))}
        </WidthAwareGridLayout>
    );
};

export default AreaGridLayout;
